import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Clock, Users } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';

interface DefaultTask {
  id: string;
  name: string;
  description: string;
  due_in_days: number;
  organization_id: string;
  created_at: string;
}

const INTERVAL_OPTIONS = [
  { value: 1, label: '1 Tag' },
  { value: 3, label: '3 Tage' },
  { value: 7, label: '7 Tage (1 Woche)' },
  { value: 14, label: '14 Tage (2 Wochen)' },
  { value: 30, label: '30 Tage (1 Monat)' },
  { value: 90, label: '90 Tage (3 Monate)' },
];

const DefaultTasksManagement = () => {
  const { user } = useAuth();
  const [defaultTasks, setDefaultTasks] = useState<DefaultTask[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [editingTask, setEditingTask] = useState<DefaultTask | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  
  // Form states
  const [taskName, setTaskName] = useState('');
  const [taskDescription, setTaskDescription] = useState('');
  const [intervalDays, setIntervalDays] = useState<number>(7);
  
  const [userOrganizationId, setUserOrganizationId] = useState<string | null>(null);

  useEffect(() => {
    fetchUserOrganization();
  }, [user]);

  useEffect(() => {
    if (userOrganizationId) {
      fetchDefaultTasks();
    }
  }, [userOrganizationId]);

  const fetchUserOrganization = async () => {
    if (!user) return;

    try {
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('organization_id')
        .eq('user_id', user.id)
        .single();

      if (error) throw error;
      setUserOrganizationId(profile.organization_id);
    } catch (error) {
      console.error('Error fetching user organization:', error);
    }
  };

  const fetchDefaultTasks = async () => {
    if (!userOrganizationId) return;

    setIsLoading(true);
    try {
      // Use any to work around TypeScript limitations until types are regenerated
      const { data, error } = await (supabase as any)
        .from('default_organization_tasks')
        .select('*')
        .eq('organization_id', userOrganizationId)
        .order('due_in_days', { ascending: true });

      if (error) throw error;
      setDefaultTasks(data || []);
    } catch (error) {
      console.error('Error fetching default tasks:', error);
      toast.error('Fehler beim Laden der Standard-Aufgaben');
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setTaskName('');
    setTaskDescription('');
    setIntervalDays(7);
    setEditingTask(null);
    setIsDialogOpen(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userOrganizationId || !taskName.trim()) return;

    setIsCreating(true);
    try {
      if (editingTask) {
        // Update existing task
        const { error } = await (supabase as any)
          .from('default_organization_tasks')
          .update({
            name: taskName.trim(),
            description: taskDescription.trim(),
            due_in_days: intervalDays
          })
          .eq('id', editingTask.id);

        if (error) throw error;
        toast.success('Standard-Aufgabe aktualisiert');
      } else {
        // Create new task
        const { error } = await (supabase as any)
          .from('default_organization_tasks')
          .insert({
            name: taskName.trim(),
            description: taskDescription.trim(),
            due_in_days: intervalDays,
            organization_id: userOrganizationId
          });

        if (error) throw error;
        toast.success('Standard-Aufgabe erstellt');
      }

      resetForm();
      fetchDefaultTasks();
      setIsDialogOpen(false);
    } catch (error) {
      console.error('Error saving default task:', error);
      toast.error('Fehler beim Speichern der Aufgabe');
    } finally {
      setIsCreating(false);
    }
  };

  const handleEdit = (task: DefaultTask) => {
    setEditingTask(task);
    setTaskName(task.name);
    setTaskDescription(task.description);
    setIntervalDays(task.due_in_days);
    setIsDialogOpen(true);
  };

  const handleDelete = async (taskId: string) => {
    try {
      const { error } = await (supabase as any)
        .from('default_organization_tasks')
        .delete()
        .eq('id', taskId);

      if (error) throw error;
      toast.success('Standard-Aufgabe gelöscht');
      fetchDefaultTasks();
    } catch (error) {
      console.error('Error deleting default task:', error);
      toast.error('Fehler beim Löschen der Aufgabe');
    }
  };

  const getIntervalLabel = (days: number) => {
    const option = INTERVAL_OPTIONS.find(opt => opt.value === days);
    return option ? option.label : `${days} Tage`;
  };

  const getIntervalColor = (days: number) => {
    if (days === 1) return 'bg-red-100 text-red-700';
    if (days <= 7) return 'bg-orange-100 text-orange-700';
    if (days <= 30) return 'bg-blue-100 text-blue-700';
    return 'bg-green-100 text-green-700';
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-8">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Standard-Organisationsaufgaben
          </CardTitle>
          <CardDescription>
            Verwalten Sie wiederkehrende Aufgaben, die alle Teammitglieder verwenden können
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => { resetForm(); setIsDialogOpen(true); }} className="mb-4">
                <Plus className="h-4 w-4 mr-2" />
                Neue Standard-Aufgabe
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>
                  {editingTask ? 'Standard-Aufgabe bearbeiten' : 'Neue Standard-Aufgabe'}
                </DialogTitle>
                <DialogDescription>
                  Diese Aufgabe kann von allen Teammitgliedern verwendet werden
                </DialogDescription>
              </DialogHeader>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="taskName">Aufgabenname</Label>
                  <Input
                    id="taskName"
                    value={taskName}
                    onChange={(e) => setTaskName(e.target.value)}
                    placeholder="z.B. Kundenkontrolle"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="taskDescription">Beschreibung/Notiz</Label>
                  <Textarea
                    id="taskDescription"
                    value={taskDescription}
                    onChange={(e) => setTaskDescription(e.target.value)}
                    placeholder="Detaillierte Beschreibung der Aufgabe..."
                    rows={3}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="interval">Wiederholungsintervall</Label>
                  <Select
                    value={intervalDays.toString()}
                    onValueChange={(value) => setIntervalDays(parseInt(value))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Intervall wählen" />
                    </SelectTrigger>
                    <SelectContent>
                      {INTERVAL_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value.toString()}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex gap-2 pt-4">
                  <Button type="submit" disabled={isCreating}>
                    {isCreating ? 'Speichert...' : editingTask ? 'Aktualisieren' : 'Erstellen'}
                  </Button>
                  {editingTask && (
                    <Button type="button" variant="outline" onClick={resetForm}>
                      Abbrechen
                    </Button>
                  )}
                </div>
              </form>
            </DialogContent>
          </Dialog>

          <div className="space-y-3">
            {defaultTasks.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Clock className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Noch keine Standard-Aufgaben erstellt</p>
                <p className="text-sm">Erstellen Sie wiederkehrende Aufgaben für Ihr Team</p>
              </div>
            ) : (
              defaultTasks.map((task) => (
                <Card key={task.id} className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="font-medium">{task.name}</h4>
                        <Badge className={getIntervalColor(task.due_in_days)}>
                          <Clock className="h-3 w-3 mr-1" />
                          {getIntervalLabel(task.due_in_days)}
                        </Badge>
                      </div>
                      {task.description && (
                        <p className="text-sm text-muted-foreground mb-2">
                          {task.description}
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground">
                        Erstellt am {new Date(task.created_at).toLocaleDateString('de-DE')}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEdit(task)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDelete(task.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </Card>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DefaultTasksManagement;